import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import type { AdminUser } from "@shared/types";

interface Props {
  open: boolean;
  user: AdminUser | null;
  currentUserId: number;
  onSaved: () => Promise<void>;
  onClose: () => void;
}

export function UserSheet({ open, user, currentUserId, onSaved, onClose }: Props) {
  const t = useT();
  const confirm = useConfirm();
  const toast = useToast();
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name);
    setErr(null);
  }, [open, user]);

  if (!open || !user) return null;

  const isSelf = user.id === currentUserId;
  const ent = user.entitlement;
  const premium = ent.tier === "premium";
  const comped = ent.source === "comp";

  function planLine(): string {
    if (!premium) return t("users.plan_free");
    const src = t(`users.source_${ent.source ?? "unknown"}`);
    const parts = [src];
    if (ent.period) parts.push(t(`paywall.${ent.period}`));
    if (ent.expires_at) {
      parts.push(t("users.until", { date: new Date(ent.expires_at * 1000).toLocaleDateString() }));
    }
    return parts.join(" · ");
  }

  async function save() {
    if (!user) return;
    setBusy(true); setErr(null);
    try {
      const patch: { name?: string } = {};
      const trimmed = name.trim();
      if (trimmed && trimmed !== user.name) patch.name = trimmed;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.updateUser(user.id, patch);
      await onSaved();
      toast.success(t("toast.saved"));
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function setComp(period: "monthly" | "yearly" | null) {
    if (!user) return;
    if (period === null && !(await confirm({
      message: t("users.confirm_revoke"), danger: true, confirmLabel: t("users.revoke"),
    }))) return;
    setBusy(true); setErr(null);
    try {
      await api.adminComp(user.id, period);
      await onSaved();
      toast.success(period ? t("users.comp_granted") : t("users.comp_revoked"));
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!user) return;
    if (!(await confirm({ message: t("users.confirm_delete"), danger: true, confirmLabel: t("common.delete") }))) return;
    setBusy(true); setErr(null);
    try {
      await api.deleteUser(user.id);
      await onSaved();
      toast.success(t("toast.deleted"));
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell variant="center" onClose={onClose}>
        <div className="flex items-center gap-2 mt-4 mb-1 flex-wrap">
          <h3 className="text-lg font-semibold">{t("users.edit_user")}</h3>
          {isSelf && (
            <span className="text-sm px-2 py-0.5 bg-surface-strong rounded-full text-fg-muted">
              {t("users.you_badge")}
            </span>
          )}
          <span className={`text-sm px-2 py-0.5 rounded-full ${
            user.role === "admin"
              ? "bg-accent-move/20 text-accent-move"
              : "bg-surface-strong text-fg-muted"
          }`}>
            {user.role === "admin" ? t("users.role_admin") : t("users.role_member")}
          </span>
        </div>
        {user.email && <p className="text-sm text-fg-muted mb-4 truncate">{user.email}</p>}

        <label className="block mb-4">
          <span className="text-sm text-fg-muted uppercase tracking-wider">{t("users.name_label")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            className="input mt-1"
          />
        </label>

        {/* Subscription — status + manual comp grant/revoke (owner tool). */}
        <div className="mb-4">
          <span className="text-sm text-fg-muted uppercase tracking-wider">{t("users.subscription")}</span>
          <div className="mt-1.5 flex items-center gap-2">
            <span className={`text-sm px-2.5 py-1 rounded-full ${
              premium ? "bg-brand/15 text-brand" : "bg-surface-strong text-fg-muted"
            }`}>
              {premium ? t("users.plan_premium") : t("users.plan_free")}
            </span>
            <span className="text-sm text-fg-muted truncate">{planLine()}</span>
          </div>
          {ent.source === "admin" ? (
            <p className="text-sm text-fg-faint mt-2">{t("users.admin_always_premium")}</p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button variant="secondary" onClick={() => setComp("monthly")} disabled={busy}>
                {t("users.grant_monthly")}
              </Button>
              <Button variant="secondary" onClick={() => setComp("yearly")} disabled={busy}>
                {t("users.grant_yearly")}
              </Button>
              {comped && (
                <Button variant="danger" onClick={() => setComp(null)} disabled={busy}>
                  {t("users.revoke")}
                </Button>
              )}
            </div>
          )}
        </div>

        {err && <p className="text-danger text-sm mt-3">{err}</p>}

        <div className="flex gap-2 mt-4 mb-2">
          <Button onClick={save} disabled={busy} className="flex-1">
            {busy ? t("common.loading") : t("edit_sheet.save")}
          </Button>
          {!isSelf && (
            <Button variant="danger" onClick={del} disabled={busy}>
              {t("users.delete_user")}
            </Button>
          )}
        </div>
    </SheetShell>
  );
}
